/**
 * Security Service
 * API client for security management endpoints
 *
 * Simplified architecture: No Google Groups, direct role assignment via Firebase Custom Claims.
 *
 * Manages:
 * - Role definitions (stored in Firestore)
 * - Staff/admin users and their roles
 */

import { api } from './apiClient';

// =============================================================================
// Role Types
// =============================================================================

export interface Role {
  id: string;
  name: string;
  description: string;
  permissions: string[];
}

export interface RoleListResponse {
  roles: Role[];
}

export interface UpdateRoleRequest {
  permissions: string[];
}

export interface CreateRoleRequest {
  id: string;
  name: string;
  description?: string;
  permissions?: string[];
}

// =============================================================================
// Permission Types
// =============================================================================

export interface Permission {
  id: string;
  name: string;
  description: string;
  category: string;
}

export interface PermissionListResponse {
  permissions: Permission[];
}

export interface CreatePermissionRequest {
  id: string;
  name: string;
  description?: string;
}

export interface UpdatePermissionRequest {
  name?: string;
  description?: string;
}

// =============================================================================
// Security User Types
// =============================================================================

export interface SecurityUser {
  uid: string;
  email: string | null;
  display_name: string | null;
  role: string | null;
  permissions: string[];
  disabled: boolean;
  email_verified: boolean;
}

export interface SecurityUserListResponse {
  users: SecurityUser[];
  total: number;
}

export interface AssignRoleRequest {
  role_id: string;
}

export interface CreateUserRequest {
  email: string;
  display_name?: string;
  role_id: string;
}

// =============================================================================
// Security Service
// =============================================================================

export const securityService = {
  // ===========================================================================
  // Role Management
  // ===========================================================================

  /**
   * List all role definitions
   */
  async listRoles(): Promise<RoleListResponse> {
    const response = await api.get<RoleListResponse>('/api/v1/security/roles');
    return response.data;
  },

  /**
   * Get a specific role by ID
   */
  async getRole(roleId: string): Promise<Role> {
    const response = await api.get<Role>(`/api/v1/security/roles/${roleId}`);
    return response.data;
  },

  /**
   * Update permissions for a role
   */
  async updateRolePermissions(
    roleId: string,
    permissions: string[]
  ): Promise<Role> {
    const response = await api.put<Role>(`/api/v1/security/roles/${roleId}`, {
      permissions,
    } as UpdateRoleRequest);
    return response.data;
  },

  /**
   * Create a new role
   */
  async createRole(request: CreateRoleRequest): Promise<Role> {
    const response = await api.post<Role>('/api/v1/security/roles', request);
    return response.data;
  },

  // ===========================================================================
  // User Management
  // ===========================================================================

  /**
   * List all staff/admin users
   */
  async listUsers(maxResults: number = 1000): Promise<SecurityUserListResponse> {
    const response = await api.get<SecurityUserListResponse>('/api/v1/security/users', {
      params: { max_results: maxResults },
    });
    return response.data;
  },

  /**
   * Get a specific user by UID
   */
  async getUser(uid: string): Promise<SecurityUser> {
    const response = await api.get<SecurityUser>(`/api/v1/security/users/${uid}`);
    return response.data;
  },

  /**
   * Assign a role to a user
   */
  async assignRole(uid: string, roleId: string): Promise<SecurityUser> {
    const response = await api.put<SecurityUser>(
      `/api/v1/security/users/${uid}/role`,
      { role_id: roleId } as AssignRoleRequest
    );
    return response.data;
  },

  /**
   * Remove role from a user
   */
  async removeRole(uid: string): Promise<SecurityUser> {
    const response = await api.delete<SecurityUser>(`/api/v1/security/users/${uid}/role`);
    return response.data;
  },

  /**
   * Disable a user account
   */
  async disableUser(uid: string): Promise<void> {
    await api.delete(`/api/v1/security/users/${uid}`);
  },

  /**
   * Provision a new staff/admin user
   */
  async createUser(request: CreateUserRequest): Promise<SecurityUser> {
    const response = await api.post<SecurityUser>('/api/v1/security/users', request);
    return response.data;
  },

  // ===========================================================================
  // Permission Management
  // ===========================================================================

  /**
   * List all permission definitions
   */
  async listPermissions(): Promise<PermissionListResponse> {
    const response = await api.get<PermissionListResponse>('/api/v1/security/permissions');
    return response.data;
  },

  /**
   * Get a specific permission by ID
   */
  async getPermission(permissionId: string): Promise<Permission> {
    const response = await api.get<Permission>(`/api/v1/security/permissions/${permissionId}`);
    return response.data;
  },

  /**
   * Create a new permission
   */
  async createPermission(request: CreatePermissionRequest): Promise<Permission> {
    const response = await api.post<Permission>('/api/v1/security/permissions', request);
    return response.data;
  },

  /**
   * Update a permission
   */
  async updatePermission(
    permissionId: string,
    request: UpdatePermissionRequest
  ): Promise<Permission> {
    const response = await api.put<Permission>(
      `/api/v1/security/permissions/${permissionId}`,
      request
    );
    return response.data;
  },

  /**
   * Delete a permission
   */
  async deletePermission(permissionId: string): Promise<void> {
    await api.delete(`/api/v1/security/permissions/${permissionId}`);
  },
};
